import { queryOptions } from '@tanstack/react-query';
import type { WorkType } from '@rundown/types';
import { api } from '../../lib/api';

/**
 * Completed (delivery) transport — released work, newest first.
 *
 * ── ASSUMED CONTRACT ────────────────────────────────────────────────────────
 *   GET /workspaces/:workspaceId/delivery/completed?projectId= -> CompletedItem[]
 *   GET /workspaces/:workspaceId/projects                             -> { id, name, archived }[]
 *
 * The second endpoint belongs to the existing `projects` CRUD module (reads
 * are open to the whole workspace — see its own controller comment); it is
 * used here only to populate the project filter with names a person
 * recognises rather than raw ids.
 *
 * Query keys are local rather than added to lib/queryKeys.ts, which other
 * agents own concurrently while this milestone is built — see that file's own
 * HARD RULE comment for why every key here still starts
 * `['ws', workspaceId, …]` regardless of which file declares it.
 */

export interface CompletedItem {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly approxHours: number;
  readonly projectId: string | null;
  readonly projectName: string | null;
  /** A request can reach Done with no sprint at all (a hotfix, or work done
   * before Planning existed), so both are nullable. */
  readonly sprintId: string | null;
  readonly sprintKey: string | null;
  readonly sprintName: string | null;
  readonly requesterName: string | null;
  readonly closedAt: string | null;
  readonly updatedAt: string;
}

export interface InternalCompletedItem extends CompletedItem {
  readonly ownerName: string | null;
  /** Noorix's list is not restricted to visible rows — see delivery.service.ts
   * — so this is what tells them, per row, whether the client can see it. */
  readonly visibleToClient: boolean;
}

/** `visibleToClient` exists only on the internal projection, the same tell
 * `isInternalRequest`/`isInternalBacklogRow` use elsewhere. */
export function isInternalCompletedItem(item: CompletedItem): item is InternalCompletedItem {
  return 'visibleToClient' in item;
}

export interface CompletedFilters {
  readonly projectId?: string;
}

function completedPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/delivery/completed`;
}

export function fetchCompleted(
  workspaceId: string,
  filters: CompletedFilters,
  signal?: AbortSignal,
): Promise<CompletedItem[]> {
  const params = new URLSearchParams();
  if (filters.projectId !== undefined) params.set('projectId', filters.projectId);
  const query = params.toString();
  return api.get<CompletedItem[]>(`${completedPath(workspaceId)}${query === '' ? '' : `?${query}`}`, signal);
}

/** Every key here starts `['ws', workspaceId]` — see lib/queryKeys.ts's HARD
 * RULE, which this local set still follows even though it is not declared in
 * that shared file. */
export const completedKeys = {
  list: (workspaceId: string, filters: CompletedFilters) =>
    ['ws', workspaceId, 'delivery', 'completed', filters.projectId ?? null] as const,
};

export function completedQueryOptions(workspaceId: string, filters: CompletedFilters) {
  return queryOptions({
    queryKey: completedKeys.list(workspaceId, filters),
    queryFn: ({ signal }) => fetchCompleted(workspaceId, filters, signal),
  });
}

export interface ProjectOption {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
}

export function fetchProjectOptions(workspaceId: string, signal?: AbortSignal): Promise<ProjectOption[]> {
  return api.get<ProjectOption[]>(`/workspaces/${encodeURIComponent(workspaceId)}/projects`, signal);
}

export const projectOptionsKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'projects', 'filter-options'] as const,
};

export function projectOptionsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: projectOptionsKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchProjectOptions(workspaceId, signal),
  });
}
